import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// THE PLAYER PROFILE ON THIS ACCOUNT (#710, #790 review).
//
// `POST /me/player` — idempotent, and the only sanctioned way to create one.
//
// ── WHY A JOURNEY NEEDS THIS AT ALL ──────────────────────────────
//
// `/auth/otp` creates an account with NO player profile, on purpose: an
// account is a person, a player is a profile, and a roster row is a club
// relationship. Keeping them apart is #710's contract.
//
// The consequence found by review: applying to a club answers
// `409 no_player`, because the join rule finds the player by `Player.userId`
// and there is none. So the exact audience the player-first door creates — a
// brand-new account with no clubs — could discover a club and not apply to it.
//
// ── THE NAME IS ASKED FOR, NOT GUESSED ──────────────────────────
//
// The account's `displayName` is null (nobody told us one), and provisioning
// falls back to the local part of the address — a machine-guessed name on a
// row other players see. So the journey that needs a profile asks for the name
// and sends it. It is used on CREATION only; this is not a rename door.
// ─────────────────────────────────────────────────────────────────────

export interface ProvisionPlayerOutcome {
  /** A machine code from the API, or null when there is now a profile. */
  code: string | null;
  /** True when this call created it; false when one already existed. */
  created: boolean;
}

export async function ensureMyPlayer(displayName: string): Promise<ProvisionPlayerOutcome> {
  let res: Awaited<ReturnType<typeof apiFetch>>;
  try {
    res = await apiFetch('/me/player', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ displayName }),
    });
  } catch {
    return { code: 'unreachable', created: false };
  }
  const body = (await res.json().catch(() => null)) as {
    error?: unknown;
    created?: unknown;
  } | null;
  if (!res.ok) {
    return {
      code: typeof body?.error === 'string' ? body.error : 'unknown_error',
      created: false,
    };
  }
  return { code: null, created: body?.created === true };
}

/**
 * The player this account IS, read back. `GET /me/player`.
 *
 * ── «NOBODY YET» AND «WE COULD NOT ASK» ARE DIFFERENT ANSWERS ─────
 *
 * The same distinction `fetchMyClubs` draws, and here it decides something
 * sharper than a screen's wording: the workspace uses this as the identity it
 * acts as (#791). A failed read reported as «no player» would leave a proven
 * person with no identity — and the one thing that must never follow from that
 * is falling back to picking one.
 *
 * So `failed` is carried separately, and the caller is expected to treat it as
 * «ask again», never as an answer.
 */
/**
 * The player this account is, as a screen renders them.
 *
 * ── WIDENED FOR «MIN STARPOINT» (#751) ─────────────────────────────
 *
 * It carried `{ id, name }`, which is what the workspace needs to act AS
 * somebody. The personal home shows them their own standing, and the endpoint
 * has always answered with it — `projectMe` returns `level`, `elo` and
 * `tournamentsPlayed`, and this client was dropping all three on the floor.
 * Reading what the server already sends beats adding a second request.
 *
 * The three numbers default to 0 rather than being optional: `projectMe` runs
 * them through `num()`, so the server never omits them, and an optional field
 * would push a «was it absent or zero» question onto every screen for a
 * distinction the API does not make.
 */
export interface MyPlayerView {
  id: string;
  name: string;
  /** Rating. 0 for a profile that has never been rated. */
  elo: number;
  /** The club-facing level derived from the rating. */
  level: number;
  tournamentsPlayed: number;
}

export interface MyPlayerResult {
  /** The account's own player, or null when the server says there is none. */
  player: MyPlayerView | null;
  failed: boolean;
}

export async function fetchMyPlayer(): Promise<MyPlayerResult> {
  let res: Response;
  try {
    res = await apiFetch('/me/player');
  } catch {
    return { player: null, failed: true };
  }
  if (!res.ok) return { player: null, failed: true };
  const body = (await res.json().catch(() => null)) as {
    player?: Record<string, unknown> | null;
  } | null;
  // A 200 whose body cannot be read is not «no player» either.
  if (!body || !('player' in body)) return { player: null, failed: true };
  const player = body.player;
  if (!player || player.id == null) return { player: null, failed: false };
  return {
    player: {
      id: String(player.id),
      name: String(player.name ?? ''),
      elo: count(player.elo),
      level: count(player.level),
      tournamentsPlayed: count(player.tournamentsPlayed),
    },
    failed: false,
  };
}

/**
 * A number from the wire, or 0.
 *
 * NOT `Number(x) || 0` — that is the same thing for every value the server
 * sends, but it also swallows `NaN` from a malformed body by calling it zero,
 * which is what this says out loud instead of implying.
 */
function count(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// ─────────────────────────────────────────────────────────────────────
// EDITING YOUR OWN PROFILE (#796).
//
// A player with an account manages their own identity — clubs are refused it
// (`player_self_managed`). `GET /me/player` carries the editable fields;
// `PATCH /me/player` changes them. The e-mail here is the PROFILE's contact
// address, what a club sees; the sign-in address is the account's and does not
// change.
// ─────────────────────────────────────────────────────────────────────

export interface MyProfile {
  name: string;
  email: string;
  phone: string;
  gender: 'M' | 'F' | '';
}

export type MyProfileResult =
  | { readonly state: 'ok'; readonly profile: MyProfile | null }
  | { readonly state: 'refused'; readonly reason: string }
  | { readonly state: 'error' };

function profileFrom(player: Record<string, unknown> | null | undefined): MyProfile | null {
  if (!player) return null;
  const g = player.gender === 'M' || player.gender === 'F' ? player.gender : '';
  return {
    name: String(player.name ?? ''),
    email: typeof player.email === 'string' ? player.email : '',
    phone: typeof player.phone === 'string' ? player.phone : '',
    gender: g,
  };
}

export async function fetchMyProfile(): Promise<MyProfileResult> {
  try {
    const res = await apiFetch('/me/player');
    if (!res.ok) return { state: 'error' };
    const body = (await res.json().catch(() => null)) as {
      player?: Record<string, unknown> | null;
    } | null;
    if (!body || !('player' in body)) return { state: 'error' };
    return { state: 'ok', profile: profileFrom(body.player) };
  } catch {
    return { state: 'error' };
  }
}

export async function updateMyProfile(profile: MyProfile): Promise<MyProfileResult> {
  try {
    const res = await apiFetch('/me/player', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: profile.name,
        email: profile.email,
        phone: profile.phone,
        gender: profile.gender,
      }),
    });
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (res.ok) {
      return { state: 'ok', profile: profileFrom(body?.player as Record<string, unknown>) };
    }
    if (res.status < 500 && typeof body?.error === 'string') {
      return { state: 'refused', reason: body.error };
    }
    return { state: 'error' };
  } catch {
    return { state: 'error' };
  }
}
